import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api, { fetchApplicationDetailBundle } from "../../services/belediyeApi";

// Mahalle detayları
export const fetchNeighborhoodDetails = createAsyncThunk(
  "belediyeApplicationDetails/fetchNeighborhoodDetails",
  async ({ neighborhoodId }, { rejectWithValue, getState }) => {
    try {
      const state = getState()?.belediyeApplicationDetails;

      // Önce store içindeki mevcut detaydan kullan
      const fromState = state?.neighborhoodDetails;
      if (
        fromState &&
        String(fromState.id) === String(neighborhoodId)
      ) {
        return fromState;
      }

      const fromApplication = state?.applicationData?.neighborhood_detail;
      if (
        fromApplication &&
        String(fromApplication.id) === String(neighborhoodId)
      ) {
        return fromApplication;
      }

      if (!neighborhoodId) return null;

      const response = await api.get(`/core/neighborhoods/${neighborhoodId}/`);
      return response.data;
    } catch {
      return rejectWithValue("Mahalle detayları alınamadı.");
    }
  }
);

// Birim detayları
export const fetchUnitDetails = createAsyncThunk(
  "belediyeApplicationDetails/fetchUnitDetails",
  async ({ unitId }, { rejectWithValue, getState }) => {
    try {
      const state = getState()?.belediyeApplicationDetails;

      // Önce store içindeki mevcut detaydan kullan
      const fromState = state?.unitDetails;
      if (fromState && String(fromState.id) === String(unitId)) {
        return fromState;
      }

      const fromApplication = state?.applicationData?.unit_detail;
      if (
        fromApplication &&
        String(fromApplication.id) === String(unitId)
      ) {
        return fromApplication;
      }

      if (!unitId) return null;

      const response = await api.get(`/common/units/${unitId}/`);
      return response.data;
    } catch {
      return rejectWithValue("Birim detayları alınamadı.");
    }
  }
);

// Başvuru detayları (YENİ: detail-bundle)
export const fetchApplicationDetails = createAsyncThunk(
  "belediyeApplicationDetails/fetchApplicationDetails",
  async ({ id }, { rejectWithValue }) => {
    try {
      const application = await fetchApplicationDetailBundle(id);
      return application;
    } catch {
      return rejectWithValue("Başvuru detayları alınamadı.");
    }
  },
  {
    condition: ({ id }, { getState }) => {
      const state = getState()?.belediyeApplicationDetails;
      const sameApplicationIsAlreadyLoading =
        state?.loading === true &&
        String(state?.currentApplicationId || "") === String(id);

      if (sameApplicationIsAlreadyLoading) {
        return false;
      }

      return true;
    },
  }
);

// Başvuruyu sil
export const deleteApplication = createAsyncThunk(
  "belediyeApplicationDetails/deleteApplication",
  async ({ id }, { rejectWithValue }) => {
    try {
      await api.delete(`/core/applications/${id}/`);
      return id;
    } catch {
      return rejectWithValue("Başvuru silinemedi.");
    }
  }
);

// Başvuruyu incelemeye al
export const reviewApplication = createAsyncThunk(
  "belediyeApplicationDetails/reviewApplication",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/core/applications/${id}/review/`, data, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      return response.data;
    } catch {
      return rejectWithValue("Başvuru inceleme işlemi başarısız.");
    }
  }
);

// Başvuru yönlendirme
export const forwardApplication = createAsyncThunk(
  "belediyeApplicationDetails/forwardApplication",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/core/applications/${id}/forward/`, data, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      return response.data;
    } catch {
      return rejectWithValue("Başvuru yönlendirme başarısız.");
    }
  }
);

// Başvuruya ait Forward Log geçmişi
export const fetchForwardLogsByApplication = createAsyncThunk(
  "belediyeApplicationDetails/fetchForwardLogsByApplication",
  async ({ applicationId }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/staff/forward-logs/?application=${applicationId}`);
      return response.data;
    } catch {
      return rejectWithValue("Yönlendirme geçmişi alınamadı.");
    }
  }
);

// Forward Log kaydı oluşturma
export const createForwardLog = createAsyncThunk(
  "belediyeApplicationDetails/createForwardLog",
  async ({ application, old_unit, new_unit, note }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/staff/forward-logs/`,
        { application, old_unit, new_unit, note },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch {
      return rejectWithValue("Yönlendirme log kaydı başarısız.");
    }
  }
);

// Başvuruyu sonuçlandır
export const finalizeApplicationStatus = createAsyncThunk(
  "belediyeApplicationDetails/finalizeApplicationStatus",
  async ({ id, status, note }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/core/applications/${id}/review/`,
        { status, note },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch {
      return rejectWithValue("Başvuru sonuçlandırılamadı.");
    }
  }
);

// Status loglarını çek
export const fetchApplicationStatusLogs = createAsyncThunk(
  "belediyeApplicationDetails/fetchApplicationStatusLogs",
  async ({ applicationId }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/staff/status-logs/?application=${applicationId}`);
      return response.data;
    } catch {
      return rejectWithValue("Başvuru logları alınamadı.");
    }
  }
);

const belediyeApplicationDetailsSlice = createSlice({
  name: "belediyeApplicationDetails",
  initialState: {
    applicationData: null,
    unitDetails: null,
    neighborhoodDetails: null,

    currentApplicationId: null,

    statusLogs: [],
    statusLogsLoading: false,
    statusLogsError: null,

    loading: false,
    error: null,

    forwardLogLoading: false,
    forwardLogError: null,
    forwardLogLast: null,

    forwardLogs: [],
    forwardLogsLoading: false,
    forwardLogsError: null,

  },
  reducers: {
    clearApplicationDetails: (state) => {
      state.applicationData = null;
      state.unitDetails = null;
      state.neighborhoodDetails = null;
      state.currentApplicationId = null;

      state.statusLogs = [];
      state.statusLogsLoading = false;
      state.statusLogsError = null;

      state.loading = false;
      state.error = null;

      state.forwardLogLoading = false;
      state.forwardLogError = null;
      state.forwardLogLast = null;

      state.forwardLogs = [];
      state.forwardLogsLoading = false;
      state.forwardLogsError = null;

    },
  },
  extraReducers: (builder) => {
    builder
      // Başvuru detayları
      .addCase(fetchApplicationDetails.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentApplicationId = action.meta.arg?.id ?? null;
      })
      .addCase(fetchApplicationDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.applicationData = action.payload || null;
        state.unitDetails = action.payload?.unit_detail || null;
        state.neighborhoodDetails = action.payload?.neighborhood_detail || null;
      })
      .addCase(fetchApplicationDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // HM notu

      // Birim detayları
      // NOT: Bunlar artık ana loading'i açıp kapatmaz.
      .addCase(fetchUnitDetails.fulfilled, (state, action) => {
        state.unitDetails = action.payload || null;
      })

      // Mahalle detayları
      // NOT: Bunlar artık ana loading'i açıp kapatmaz.
      .addCase(fetchNeighborhoodDetails.fulfilled, (state, action) => {
        state.neighborhoodDetails = action.payload || null;
      })

      // Başvuru sonuçlandırma
      .addCase(finalizeApplicationStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(finalizeApplicationStatus.fulfilled, (state, action) => {
        state.loading = false;
        if (state.applicationData) {
          state.applicationData.status = action.meta.arg.status;
          state.applicationData.finalNote = action.meta.arg.note;
        }
      })
      .addCase(finalizeApplicationStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Status logları
      .addCase(fetchApplicationStatusLogs.pending, (state) => {
        state.statusLogsLoading = true;
        state.statusLogsError = null;
      })
      .addCase(fetchApplicationStatusLogs.fulfilled, (state, action) => {
        state.statusLogsLoading = false;
        state.statusLogs = action.payload || [];
      })
      .addCase(fetchApplicationStatusLogs.rejected, (state, action) => {
        state.statusLogsLoading = false;
        state.statusLogsError = action.payload;
      })

      // Başvuru yönlendirme
      .addCase(forwardApplication.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forwardApplication.fulfilled, (state, action) => {
        state.loading = false;
        if (state.applicationData) {
          state.applicationData = {
            ...state.applicationData,
            ...action.payload,
          };
        }
      })
      .addCase(forwardApplication.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Forward log create
      .addCase(createForwardLog.pending, (state) => {
        state.forwardLogLoading = true;
        state.forwardLogError = null;
      })
      .addCase(createForwardLog.fulfilled, (state, action) => {
        state.forwardLogLoading = false;
        state.forwardLogLast = action.payload;
      })
      .addCase(createForwardLog.rejected, (state, action) => {
        state.forwardLogLoading = false;
        state.forwardLogError = action.payload;
      })

      // Forward log listesi
      .addCase(fetchForwardLogsByApplication.pending, (state) => {
        state.forwardLogsLoading = true;
        state.forwardLogsError = null;
      })
      .addCase(fetchForwardLogsByApplication.fulfilled, (state, action) => {
        state.forwardLogsLoading = false;
        state.forwardLogs = action.payload || [];
      })
      .addCase(fetchForwardLogsByApplication.rejected, (state, action) => {
        state.forwardLogsLoading = false;
        state.forwardLogsError = action.payload;
      })

      // Başvuru silme
      .addCase(deleteApplication.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteApplication.fulfilled, (state) => {
        state.loading = false;
        state.applicationData = null;
        state.unitDetails = null;
        state.neighborhoodDetails = null;
      })
      .addCase(deleteApplication.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Başvuruyu incelemeye alma
      .addCase(reviewApplication.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(reviewApplication.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(reviewApplication.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearApplicationDetails } =
  belediyeApplicationDetailsSlice.actions;

export default belediyeApplicationDetailsSlice.reducer;